import {
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { IProducts as IGcfProducts } from 'gcf-components/interfaces';
import { concat, first, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import {
  IProduct,
  IProductVariation,
  PaginationResponse,
} from 'src/interfaces';
import {
  generateQuery,
  mapProductsToDatasourceJSON,
  queryKeys,
  requestCCApi,
} from 'src/utils';

import { fetchThemes } from '../themes';
import { mapProductsWithCategories } from './helper';
import { fetchGetProductCategories } from './productCategories';
import { fetchProducts } from './useGetProducts';

export const useGetDatasourceProducts = (
  productFilters: string[] = [],
  productVariationFilters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<IGcfProducts>,
) => {
  return useQuery({
    queryKey: queryKeys.products.all([
      'isDatasource:true',
      ...productFilters,
      ...productVariationFilters,
    ]),
    queryFn: () =>
      fetchDatasourceProducts(productFilters, productVariationFilters),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

const fetchDatasourceProducts = async (
  productFilters: string[] = [],
  productVariationFilters: string[] = [],
) => {
  const [products, themes] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: queryKeys.products.all([
        ...productFilters,
        ...productVariationFilters,
      ]),
      queryFn: () => fetchProducts(productFilters, productVariationFilters),
      staleTime: Infinity,
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.themes.all(),
      queryFn: () => fetchThemes(),
      staleTime: Infinity,
    }),
  ]);

  return mapProductsToDatasourceJSON(products, themes);
};

export const useGetInfiniteProductsWithVariations = (
  filters: string[] = [],
) => {
  return useInfiniteQuery(
    queryKeys.products.pagination(filters),
    ({ pageParam = 1 }) =>
      fetchInfiniteProductsWithVariations(pageParam, filters),
    {
      getNextPageParam: (lastPage, allPages) => {
        const currentPage = lastPage ? allPages.length : 1;
        const totalPages = lastPage ? lastPage.totalPages : 1;

        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
      enabled: !isEmpty(filters),
      staleTime: Infinity,
    },
  );
};

export const useGetPaginatedProductsWithVariations = (
  page: number,
  filters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<PaginationResponse<IProduct[]>>,
) => {
  return useQuery({
    queryKey: queryKeys.products.pagination([page, ...filters]),
    queryFn: async () => {
      const response = await fetchInfiniteProductsWithVariations(page, filters);

      return {
        data: response.products,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
      };
    },
    enabled: !isEmpty(filters),
    ...overrideQuerySetting,
  });
};

export const fetchInfiniteProductsWithVariations = async (
  page: number,
  filters: string[] = [],
) => {
  const url = `/viewProductsWithVariations?${filters.join(
    '&',
  )}&page=${page}&count=true`;

  const [response, productCategories] = await Promise.all([
    requestCCApi({ url }),
    queryClient.fetchQuery({
      queryKey: queryKeys.productCategories.all(['sort=*name']),
      queryFn: () => fetchGetProductCategories(['sort=*name']),
      staleTime: Infinity,
    }),
  ]);

  return {
    products: mapProductsWithCategories(response, productCategories),
    totalPages: response.totalPages ?? 0,
    totalCount: response.totalCount ?? 0,
  };
};

export const useGetProductWithVariationsComplete = (
  {
    productCode,
    variationCode,
  }: { productCode?: string; variationCode?: string },
  overrideQuerySetting?: UseQueryOptions<IProduct | undefined>,
) => {
  const filter = productCode
    ? productCode.includes('|')
      ? `product_code.list=${productCode}`
      : `product_code=${productCode}`
    : variationCode?.includes('|')
    ? `productVariations.product_code.list=${variationCode}`
    : `productVariations.product_code=${variationCode}`;

  return useQuery({
    queryKey: queryKeys.productVariations.all([filter]),
    queryFn: () => fetchProductWithVariationsComplete(filter),
    enabled: !!productCode || !!variationCode,
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

const fetchProductWithVariationsComplete = async (
  filter: string,
  productCode?: string,
): Promise<IProduct | undefined> => {
  const fetchProductsAsync = async (productCode: string) =>
    first(
      await fetchProducts([
        productCode.includes('|')
          ? `product_code.list=${productCode}`
          : `product_code=${productCode}`,
      ]),
    );
  if (!!productCode) {
    // request productVariation directly if group product code provided
    return fetchProductsAsync(productCode);
  }

  const viewUrl = `/viewProductsWithVariations?${filter}&pageSize=1`;
  const viewResponse = await requestCCApi({ url: viewUrl });
  // get group product code from view response because only the variation code is know
  const groupProductCode = viewResponse.data?.[0]?.product_code;

  if (!groupProductCode) {
    return undefined;
  }

  return fetchProductsAsync(groupProductCode);
};

export { mapProductVariationsWithTemplate } from './useGetProduct';

export const useGetAllProductsWithVariations = (
  filters: string[] = [],
  overrideQuerySetting?: UseInfiniteQueryOptions<
    PaginationResponse<IProduct[]>
  >,
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.productVariations.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchAllProductsWithVariations(
        // ! filters product_code as string, ex will be [1, 11, 2] not [1, 2, 11]
        concat(filters, [`page=${pageParam}`]),
      ),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    ...overrideQuerySetting,
  });
};

const fetchAllProductsWithVariations = async (
  filters: string[] = [],
): Promise<PaginationResponse<IProduct[]>> => {
  const url = `/productVariations${generateQuery([], filters)}`;
  const response = await requestCCApi({ url });

  return {
    data: response.data,
    totalPages: response.totalPages ?? 0,
    totalCount: response.totalCount ?? 0,
  } as PaginationResponse<IProduct[]>;
};

export const useGetProductsWithVariations = (
  filters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<IProductVariation[]>,
) => {
  return useQuery({
    queryKey: queryKeys.productVariations.all([...filters, 'pageSize=0']),
    queryFn: () => fetchProductsWithVariations(filters),
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

const fetchProductsWithVariations = async (
  filters: string[] = [],
): Promise<IProductVariation[]> => {
  const url = `/productVariations${generateQuery(
    [],
    concat(filters, 'pageSize=0'),
  )}`;
  const response = await requestCCApi({ url });

  return response.data;
};
